import { Flex, Title, Button } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { INITIAL_VALUES } from './constants';
import { FormTextField, FormRichTextEditor } from '@shared/ui';
import { FormGuideScheduleWork } from '@entities/scheduleWork';
import { FormGuideEmploymentType } from '@entities/employmentType';
import { FormSalarySelectField } from '@widgets/form';
import {
  ValidateCreateVacanciesTemplateSchema,
  type VacanciesTemplatesFormValues,
} from './types';
import { FormTagsInputSkills } from '@entities/skill/components/FormTagsInputSkills';
import { useParams } from 'react-router';

type VacanciesTemplatesFormProps = {
  initialValues?: VacanciesTemplatesFormValues;
  onBackHandler: (isDirty: boolean) => void;
  onSubmit: (values: VacanciesTemplatesFormValues) => void;
  loading: boolean;
  disabled: boolean;
};

export const VacanciesTemplatesForm = ({
  initialValues = INITIAL_VALUES,
  onBackHandler,
  onSubmit,
  loading,
  disabled,
}: VacanciesTemplatesFormProps) => {
  const { id } = useParams();

  const form = useForm({
    initialValues,
    validate: zodResolver(ValidateCreateVacanciesTemplateSchema),
  });

  const handleCancel = () => {
    onBackHandler(form.isDirty());
  };

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Flex direction='column' gap={32}>
        <Flex gap={24} direction='column'>
          <Title order={4}>Основная информация</Title>

          <FormTextField
            title='Название вакансии'
            description='Введите название должности, на которую Синтия будет искать лучшего кандидата'
            placeholder='Введите название'
            containerProps={{
              pt: 0,
            }}
            key={form.key('name')}
            {...form.getInputProps('name')}
            error={form.errors.name}
          />
        </Flex>

        <Flex gap={24} direction='column'>
          <Title order={4}>Условия работы</Title>

          <Flex direction='column'>
            <FormGuideScheduleWork
              key={form.key('scheduleWorkIds')}
              {...form.getInputProps('scheduleWorkIds')}
              error={form.errors.scheduleWorkIds}
            />
            <FormGuideEmploymentType
              key={form.key('employmentTypeIds')}
              {...form.getInputProps('employmentTypeIds')}
              error={form.errors.employmentTypeIds}
            />

            <FormTagsInputSkills
              key={form.key('skills')}
              {...form.getInputProps('skills')}
              error={form.errors.skills}
            />

            <FormSalarySelectField
              title='Уровень дохода'
              description='Важно указать реальный желаемый диапазон, чтобы ваш запрос получил больше откликов.'
              propsFromInput={{
                ...form.getInputProps('salaryFrom'),
                error: form.errors.salaryFrom,
              }}
              propsToInput={{
                ...form.getInputProps('salaryTo'),
                error: form.errors.salaryTo,
              }}
              propsCurrencyInput={{
                ...form.getInputProps('currencyId'),
                error: form.errors.currencyId,
              }}
              propsPaymentPeriodInput={{
                ...form.getInputProps('paymentPeriodId'),
                error: form.errors.paymentPeriodId,
              }}
            />

            <FormRichTextEditor
              title='Обязательные требования к кандидату'
              description='Укажите здесь только самые важные требования к кандидату, на которые Синтия должна ориентироваться в выборе. Кандидаты не соответствующие этим требованиям она не будет рассматривать. Например, для водителя обязательно нужны водительские права.'
              placeholder='Введите описание'
              key={form.key('mandatoryRequirements')}
              {...form.getInputProps('mandatoryRequirements')}
            />
            <FormRichTextEditor
              title='Дополнительные требования к кандидату'
              description='Добавьте специальные требования, важные для Вас, чтобы Синтия смогла уточнить их у кандидата и дать выбрать для Вас лучшего из лучших. Например, кандидат должен знать иностранный язык или иметь опыт работы в необходимой области более 5 лет, подтвержденный рекомендациями'
              placeholder='Введите описание'
              key={form.key('additionalRequirements')}
              {...form.getInputProps('additionalRequirements')}
            />
            <FormRichTextEditor
              title='Трудовые обязанности'
              description='Опишите, какие задачи должен будет выполнять работник на работе, за что отвечать'
              placeholder='Введите описание'
              key={form.key('responsibilities')}
              {...form.getInputProps('responsibilities')}
              containerProps={{
                mb: 0,
              }}
            />
          </Flex>
        </Flex>

        <Flex gap={8}>
          <Button variant='gray' onClick={handleCancel}>
            Назад
          </Button>
          <Button type='submit' loading={loading} disabled={disabled}>
            {id ? 'Сохранить' : 'Создать'}
          </Button>
        </Flex>
      </Flex>
    </form>
  );
};
